import {
    DashboardOutlined,
    GroupOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    ProductOutlined,
    RobotOutlined,
    ShopOutlined,
    TableOutlined,
    UserOutlined,
} from '@ant-design/icons';
import {Button, Flex, Layout, Menu, MenuProps, theme} from 'antd';
import {Content, Header} from 'antd/es/layout/layout';
import Sider from 'antd/es/layout/Sider';
import React, {useState} from 'react';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';

import {useAuth} from '../../AuthContext';
import {useScreenInfo} from '../../ResponsiveGate';
import {EbaySignInBanner} from '../pages/ebay/EbaySignInBanner';
import styles from './styles.module.less';

enum MenuItemId {
    DASHBOARD = 'dashboard',
    PRODUCTS = 'products',
    GROUP_SALES = 'group-sales',
    PALLET_ORDERS = 'pallet-orders',
    USERS = 'users',
    WORKER = 'worker',
    EBAY = 'ebay',
}

export const AppLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {screenMap} = useScreenInfo();

    const [collapsed, setCollapsed] = useState(!screenMap.lg);
    const [collapsedWidth, setCollapsedWidth] = useState<number | undefined>(!screenMap.lg ? 0 : undefined);

    const auth = useAuth();

    const {
        token: {colorBgContainer, borderRadiusLG},
    } = theme.useToken();
    const items: MenuProps['items'] = [
        {
            key: 'dashboard',
            icon: React.createElement(DashboardOutlined),
            label: 'Dashboard',
            onClick: () => navigate('/dashboard'),
        },
        {
            key: 'products',
            icon: React.createElement(ProductOutlined),
            label: 'Products',
            onClick: () => navigate('/products'),
        },
        {
            key: 'group-sales',
            icon: React.createElement(GroupOutlined),
            label: 'Group Sales',
            onClick: () => navigate('/group-sales'),
        },
        {
            key: 'pallet-orders',
            icon: React.createElement(TableOutlined),
            label: 'Pallet Orders',
            onClick: () => navigate('/pallet-orders'),
        },
        {
            key: '3',
            icon: React.createElement(UserOutlined),
            label: 'Users',
            onClick: () => navigate('/users'),
        },
        {
            key: MenuItemId.WORKER,
            icon: React.createElement(RobotOutlined),
            label: 'Worker',
            onClick: () => navigate('/worker'),
        },
        {
            key: MenuItemId.EBAY,
            icon: React.createElement(ShopOutlined),
            label: 'eBay',
            onClick: () => navigate('/ebay'),
        },
    ];

    const siderStyle: React.CSSProperties = {
        overflow: 'auto',
        height: '100vh',
        position: screenMap.lg ? 'sticky' : 'fixed',
        zIndex: 10,
        marginTop: screenMap.lg ? -44 : 0,
        paddingTop: 44,
        insetInlineStart: 0,
        top: 0,
        bottom: 0,
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
    };

    return (
        <Layout style={{background: '#f5f5f5', minHeight: '100vh'}}>
            <Header
                style={{
                    padding: 0,
                    background: colorBgContainer,
                    position: screenMap.lg ? 'sticky' : 'fixed',
                    top: 0,
                    zIndex: 11,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    height: 44,
                }}
            >
                <Button
                    type="text"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => setCollapsed(!collapsed)}
                    style={{
                        fontSize: '16px',
                        width: 36,
                        height: 36,
                        marginLeft: 10,
                    }}
                />
                <Flex
                    justify={'center'}
                    className={styles.demoLogo}
                    align={'center'}
                >
                    <div className={styles.logo}>Sunshine Auction</div>
                </Flex>
            </Header>

            <Layout>
                <Sider
                    style={siderStyle}
                    color={'white'}
                    theme={'light'}
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    defaultCollapsed={screenMap.lg}
                    className="no-print"
                    breakpoint="lg"
                    collapsedWidth={collapsedWidth}
                    onBreakpoint={(broken) => {
                        if (broken) {
                            setCollapsedWidth(0);
                        } else {
                            setCollapsedWidth(undefined);
                        }
                        setCollapsed(broken);
                    }}
                >
                    <Flex
                        justify={'space-between'}
                        align={'center'}
                        vertical
                        style={{height: '100%', paddingBottom: 20}}
                    >
                        <Menu
                            mode="inline"
                            defaultSelectedKeys={[MenuItemId.DASHBOARD]}
                            selectedKeys={[mapHttpAddressToMenuItemId(location.pathname)]}
                            onClick={() => {
                                if (!screenMap.lg) {
                                    setCollapsed(true);
                                }
                            }}
                            items={items}
                        />
                        <Button onClick={auth?.logout}>Log out</Button>
                    </Flex>
                </Sider>
                <Layout>
                    <Content
                        className={styles.layout}
                        style={{borderRadius: borderRadiusLG, paddingTop: screenMap.lg ? 24 : 54}}
                    >
                        <EbaySignInBanner />
                        <Outlet />
                    </Content>
                </Layout>
            </Layout>
        </Layout>
    );
};

export function mapHttpAddressToMenuItemId(pathname: string): string {
    // The index route renders the dashboard page.
    if (pathname === '/' || pathname.startsWith('/dashboard')) {
        return MenuItemId.DASHBOARD;
    }
    if (pathname.includes('/products')) {
        return MenuItemId.PRODUCTS;
    }
    if (pathname.includes('/group-sales')) {
        return MenuItemId.GROUP_SALES;
    }
    if (pathname.includes('/pallet-orders')) {
        return MenuItemId.PALLET_ORDERS;
    }
    if (pathname.includes('/users')) {
        return MenuItemId.USERS;
    }
    if (pathname.startsWith('/worker')) {
        return MenuItemId.WORKER;
    }
    if (pathname.startsWith('/ebay')) {
        return MenuItemId.EBAY;
    }
    return '';
}
